/**
 * V111: MM 프로젝트 메모 저장 Hook
 *
 * saveMmProjectNote Cloud Function을 호출하여
 * 프로젝트별 월간 메모를 저장합니다.
 *
 * @module shared/hooks/useMmProjectNoteMutation
 */

import { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import {
  SaveMmProjectNoteRequest,
  SaveMmProjectNoteResponse,
} from '../types/mm';

/**
 * MM 프로젝트 메모 저장 훅 반환 타입
 */
export interface UseMmProjectNoteMutationResult {
  /** 메모 저장 함수 */
  saveNote: (request: SaveMmProjectNoteRequest) => Promise<SaveMmProjectNoteResponse>;

  /** 저장 중 상태 */
  isSaving: boolean;

  /** 에러 */
  error: Error | null;
}

/**
 * MM 프로젝트 메모 저장 Hook
 *
 * @returns 저장 함수 및 상태
 *
 * @example
 * ```typescript
 * function MmRegisterPage() {
 *   const { saveNote, isSaving } = useMmProjectNoteMutation();
 *
 *   const handleSave = async (teamId: string, projectId: string) => {
 *     await saveNote({
 *       teamId,
 *       projectId,
 *       month: '2026-01',
 *       workDetails: '업무 상세',
 *       remarks: '특이사항',
 *       costOccurred: '비용발생',
 *     });
 *   };
 * }
 * ```
 */
export function useMmProjectNoteMutation(): UseMmProjectNoteMutationResult {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const saveNote = useCallback(async (
    request: SaveMmProjectNoteRequest
  ): Promise<SaveMmProjectNoteResponse> => {
    if (!functions) {
      throw new Error('Firebase Functions not initialized');
    }

    try {
      setIsSaving(true);
      setError(null);

      const fn = httpsCallable<SaveMmProjectNoteRequest, SaveMmProjectNoteResponse>(
        functions,
        'saveMmProjectNote'
      );

      const result = await fn(request);

      if (result.data.success) {
        return result.data;
      } else {
        throw new Error(result.data.error || 'MM 메모 저장 실패');
      }
    } catch (err: any) {
      console.error('[V111] useMmProjectNoteMutation error:', err);
      setError(err);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, []);

  return {
    saveNote,
    isSaving,
    error,
  };
}
